import clsx from 'clsx';
import React, { ReactNode, useState, useRef, useEffect } from 'react';

import AnchorLink from '@/src/components/common/Elements/AnchorLink/AnchorLink';
import Icon, {
  ICONS,
  ICON_TYPES,
} from '@/src/components/common/Elements/Icon/Icon';
import {
  ACTION_POSITIONS,
  MODAL_TYPE,
} from '@/src/components/common/Elements/Modal/Config';
import Modal from '@/src/components/common/Elements/Modal/Modal';
import ModalButton from '@/src/components/common/Elements/Modal/ModalButton';
import { cn } from '@/src/util/ui';

type CustomButtonProps = {
  label: string;
  disabled?: boolean;
};

type ModalPopupProps = {
  id?: string;
  title: React.ReactNode;
  // icon.icon can be a material icon string or a component/function that renders an SVG
  icon?: { icon: string | any; iconType?: string; className?: string };
  className?: string;
  message?: string;
  modalWidth?: string;
  modalType?;
  actionPosition?;
  open?: boolean;
  onClose: (event?: any) => void;
  onConfirm: (event?: any) => void;
  cancelBtn?: boolean | CustomButtonProps;
  confirmBtn?: boolean | CustomButtonProps;
  children?: ReactNode;
};

const ModalPopup = ({
  id = '',
  title = null,
  icon = { icon: ICONS.info, iconType: ICON_TYPES.outlined, className: '' },
  className,
  modalWidth = 'tw-w-[690px]',
  modalType = MODAL_TYPE.default,
  actionPosition = ACTION_POSITIONS.between,
  message = 'Do you wish to proceed?',
  cancelBtn = null,
  confirmBtn = null,
  open = true,
  onClose,
  onConfirm,
  children,
}: ModalPopupProps) => {
  const {
    headerTextClasses,
    closeIconClasses,
    btnConfirmClasses,
    modalClasses,
  } = modalType;

  const [showTooltip, setShowTooltip] = useState(false);

  if (!open) {
    return null;
  }

  //  modal body content
  const body = children ? (
    children
  ) : (
    <div className={clsx('tw-text-cfo-black', 'tw-m-0')}>{message}</div>
  );

  //   modal footer actions
  const cancelButton = typeof cancelBtn === 'object' && (
    <ModalButton
      className={cn(
        'btn--cancel',
        'tw-text-cfo-black',
        'tw-mr-[5px]',
        'tw-border-cfo-grey-500 tw-bg-cfo-grey-100'
      )}
      // className={clsx('btn--cancel', 'tw-text-cfo-black', '!tw-border-cfo-grey-500 tw-bg-white')}
      btn={cancelBtn}
      label={(cancelBtn && cancelBtn.label) || 'Cancel'}
      onClick={onClose}
    />
  );
  const confirmButton = typeof confirmBtn === 'object' && (
    <ModalButton
      className={cn(
        'btn--confirm',
        confirmBtn && confirmBtn.disabled
          ? 'tw-text-cfo-grey-600 tw-border-cfo-grey-500 tw-bg-cfo-grey-100'
          : btnConfirmClasses
      )}
      btn={confirmBtn}
      label={(confirmBtn && confirmBtn.label) || 'Confirm'}
      onClick={onConfirm}
      disabled={confirmBtn && confirmBtn.disabled}
    />
  );

  return (
    <Modal id={id} className={cn(className)}>
      {/* Note: !important is used on some tailwind classes here to override the default styles in vendor.css */}
      <div
        className={cn(
          'modal-dialog',
          'tw-absolute tw-top-[50%] tw-left-1/2 !tw-translate-y-[-50%] !tw-translate-x-[-50%]',
          modalWidth
        )}
        role="document"
      >
        <div className={clsx('modal-content', '!tw-rounded-[5px]')}>
          {/* Modal Header */}
          <div
            className={cn(
              'modal-header',
              'tw-border-b-0',
              'tw-p-[20px]',
              'tw-rounded-t-[5px]',
              modalClasses
            )}
          >
            <div className={clsx('tw-flex tw-items-center tw-justify-between')}>
              <p
                className={cn(
                  'tw-flex tw-items-center',
                  'tw-text-[18px] tw-font-brandonTextMedium',
                  'tw-m-0 ',
                  headerTextClasses ? headerTextClasses : 'tw-text-cfo-black '
                )}
              >
                {icon && (
                  <span
                    className="tw-relative tw-inline-block tw-mr-[5px]"
                    ref={(iconRef) => {
                      /* placeholder for ref */
                    }}
                  >
                    <Icon
                      icon={icon.icon}
                      iconType={icon.iconType}
                      className={clsx('tw-text-[20px]', icon.className)}
                      onMouseEnter={() => setShowTooltip(true)}
                      onMouseLeave={() => setShowTooltip(false)}
                      onClick={() => setShowTooltip((s) => !s)}
                      role="button"
                      tabIndex={0}
                    />
                    {showTooltip && (
                      <div className="tw-absolute tw-bottom-full tw-left-1/2 tw--translate-x-1/2 tw-mb-2 tw-bg-black tw-text-white tw-px-3 tw-py-2 tw-rounded tw-text-sm tw-whitespace-nowrap tw-z-[100000]">
                        Connect and add up to 8 WhatsApp participants.
                      </div>
                    )}
                  </span>
                )}

                {title}
              </p>
              <AnchorLink
                // className={cn(headerTextClasses ? headerTextClasses : 'tw-text-cfo-black ')}
                className={cn(
                  closeIconClasses ? closeIconClasses : 'tw-text-cfo-black ',
                  'tw-leading-none'
                )}
                data-dismiss="modal"
                aria-label="Close"
                onClick={onClose}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    onClose();
                  }
                }}
              >
                <Icon
                  className={cn(
                    'tw-text-[22px]',
                    closeIconClasses ? closeIconClasses : 'tw-text-cfo-black '
                  )}
                  icon={ICONS.close}
                />
              </AnchorLink>
            </div>
          </div>

          {/* Modal Body */}
          <div
            className={clsx(
              'modal-body',
              'tw-p-[20px]',
              'tw-border-solid tw-border-t-[1px] tw-border-t-cfo-grey-500'
            )}
          >
            {body}
          </div>

          {/* Modal Footer */}
          <div
            className={clsx(
              'modal-footer',
              'tw-py-[15px] tw-px-[17px]',
              '!tw-border-t-cfo-grey-500'
            )}
          >
            <div className={cn('tw-flex tw-items-center', actionPosition)}>
              {/* Footer Action: Cancel button */}
              {cancelButton}

              {/* Footer Action: Confirm button */}
              {confirmButton}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ModalPopup;
